// Medical Scores Database - Infectious Disease, Emergency & Critical Care
// Additional scores for infection, emergency medicine and ICU

import {
  Thermometer,
  Activity,
  AlertTriangle,
  Stethoscope,
  Droplets,
  Brain,
  Zap,
  Wind
} from 'lucide-react';

// ==================== INFECTIOLOGIE ÉTENDUE ====================
export const INFECTIOUS_EXTENDED_SCORES = {
  qsofa: {
    name: 'qSOFA',
    category: 'Infectiologie',
    icon: Thermometer,
    description: 'Dépistage rapide sepsis',
    color: 'rose',
    fields: [
      { id: 'rr', label: 'FR ≥ 22/min', type: 'checkbox', points: 1 },
      { id: 'gcs', label: 'Altération conscience (GCS < 15)', type: 'checkbox', points: 1 },
      { id: 'sbp', label: 'PAS ≤ 100 mmHg', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.rr) total += 1;
      if (values.gcs) total += 1;
      if (values.sbp) total += 1;
      return { score: total, max: 3 };
    },
    interpret: (score) => {
      if (score < 2) return { level: 'success', text: `qSOFA ${score}/3`, detail: 'Risque faible. Surveillance standard' };
      return { level: 'danger', text: `qSOFA ${score}/3`, detail: 'Suspicion sepsis. Évaluer SOFA complet, antibiothérapie' };
    },
    formula: 'qSOFA ≥ 2 = suspicion sepsis (Se 70%, Sp 80%)'
  },

  sris: {
    name: 'Critères SRIS (SEPSIS 1-2)',
    category: 'Infectiologie',
    icon: Thermometer,
    description: 'Syndrome de réponse inflammatoire systémique',
    color: 'rose',
    fields: [
      { id: 'temp', label: 'Température > 38°C ou < 36°C', type: 'checkbox', points: 1 },
      { id: 'hr', label: 'FC > 90/min', type: 'checkbox', points: 1 },
      { id: 'rr', label: 'FR > 20/min ou PaCO₂ < 32 mmHg', type: 'checkbox', points: 1 },
      { id: 'wbc', label: 'GB > 12000 ou < 4000 ou > 10% immatures', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 4 };
    },
    interpret: (score) => {
      if (score < 2) return { level: 'success', text: `SRIS ${score}/4 critères`, detail: 'Pas de SRIS' };
      return { level: 'warning', text: `SRIS ${score}/4 critères`, detail: 'SRIS présent (≥ 2 critères). Si infection: sepsis (ancienne définition)' };
    },
    formula: 'SRIS ≥ 2 critères. Ancien critère de sepsis (SEPSIS 1-2)'
  },

  meds: {
    name: 'Score MEDS',
    category: 'Infectiologie',
    icon: Thermometer,
    description: 'Mortalité sur sepsis aux urgences',
    color: 'rose',
    fields: [
      { id: 'terminal', label: 'Maladie terminale (espérance < 30j)', type: 'checkbox', points: 6 },
      { id: 'tachypnea', label: 'Tachypnée ou hypoxémie', type: 'checkbox', points: 3 },
      { id: 'choc', label: 'Choc septique', type: 'checkbox', points: 3 },
      { id: 'platelets', label: 'Plaquettes < 150 000/mm³', type: 'checkbox', points: 3 },
      { id: 'bands', label: 'Formes immatures > 5%', type: 'checkbox', points: 3 },
      { id: 'age', label: 'Âge > 65 ans', type: 'checkbox', points: 3 },
      { id: 'nursing', label: 'Infection des voies respiratoires basses', type: 'checkbox', points: 2 },
      { id: 'nursing_home', label: 'Résident en institution', type: 'checkbox', points: 2 },
      { id: 'confusion', label: 'Altération état mental', type: 'checkbox', points: 2 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.terminal) total += 6;
      if (values.tachypnea) total += 3;
      if (values.choc) total += 3;
      if (values.platelets) total += 3;
      if (values.bands) total += 3;
      if (values.age) total += 3;
      if (values.nursing) total += 2;
      if (values.nursing_home) total += 2;
      if (values.confusion) total += 2;
      return { score: total, max: 27 };
    },
    interpret: (score) => {
      if (score <= 4) return { level: 'success', text: `MEDS ${score}`, detail: 'Mortalité à 28j: 1%' };
      if (score <= 7) return { level: 'warning', text: `MEDS ${score}`, detail: 'Mortalité à 28j: 4%' };
      if (score <= 12) return { level: 'warning', text: `MEDS ${score}`, detail: 'Mortalité à 28j: 9%' };
      if (score <= 15) return { level: 'danger', text: `MEDS ${score}`, detail: 'Mortalité à 28j: 16%' };
      return { level: 'danger', text: `MEDS ${score}`, detail: 'Mortalité à 28j: 39%' };
    },
    formula: 'MEDS = Mortality in Emergency Department Sepsis'
  },

  hoen: {
    name: 'Score de Hoen',
    category: 'Infectiologie',
    icon: Brain,
    description: 'Méningite bactérienne vs virale',
    color: 'rose',
    fields: [
      { id: 'glucose', label: 'Glycorachie (mmol/L)', type: 'number', min: 0, max: 10, step: 0.1 },
      { id: 'protein', label: 'Protéinorachie (g/L)', type: 'number', min: 0.1, max: 10, step: 0.1 },
      { id: 'wbc', label: 'Leucocytes LCR (/mm³)', type: 'number', min: 0, max: 50000 },
      { id: 'bloodWbc', label: 'Leucocytes sang (×10⁹/L)', type: 'number', min: 0, max: 50, step: 0.1 }
    ],
    calculate: (values) => {
      const glucose = values.glucose || 2;
      const protein = values.protein || 1;
      const wbc = values.wbc || 500;
      const bloodWbc = values.bloodWbc || 10;
      
      // Formule de Hoen simplifiée
      // Si glycorachie basse, protéinorachie haute, cellularité LCR élevée → bactérienne
      let score = 0;
      if (glucose < 2) score += 2;
      if (protein > 1) score += 1;
      if (wbc > 1000) score += 2;
      if (bloodWbc > 15) score += 1;
      
      return { score, max: 6, details: { glucose, protein, wbc } };
    },
    interpret: (score) => {
      if (score <= 1) return { level: 'success', text: `Hoen ${score}`, detail: 'Méningite virale probable' };
      if (score <= 3) return { level: 'warning', text: `Hoen ${score}`, detail: 'Zone intermédiaire. Antibiothérapie à discuter' };
      return { level: 'danger', text: `Hoen ${score}`, detail: 'Méningite bactérienne probable. Antibiothérapie urgente' };
    },
    formula: 'Hoen: critères LCR orientant vers origine bactérienne ou virale'
  },

  dakarTetanos: {
    name: 'Score de Dakar (Tétanos)',
    category: 'Infectiologie',
    icon: Thermometer,
    description: 'Gravité du tétanos',
    color: 'rose',
    fields: [
      { id: 'incubation', label: 'Durée incubation', type: 'select', options: [
        { value: 0, label: '0 - > 7 jours' }, { value: 1, label: '1 - ≤ 7 jours' }
      ]},
      { id: 'invasion', label: 'Durée d\'invasion', type: 'select', options: [
        { value: 0, label: '0 - > 2 jours' }, { value: 1, label: '1 - ≤ 2 jours' }
      ]},
      { id: 'entry', label: 'Porte d\'entrée', type: 'select', options: [
        { value: 0, label: '0 - Autre' }, { value: 1, label: '1 - Utérus, brûlure, chirurgie, fracture ouverte' }
      ]},
      { id: 'spasms', label: 'Paroxysmes', type: 'select', options: [
        { value: 0, label: '0 - Absents' }, { value: 1, label: '1 - Présents' }
      ]},
      { id: 'temp', label: 'Température rectale', type: 'select', options: [
        { value: 0, label: '0 - < 38.4°C' }, { value: 1, label: '1 - ≥ 38.4°C' }
      ]},
      { id: 'hr', label: 'Fréquence cardiaque adulte', type: 'select', options: [
        { value: 0, label: '0 - < 120/min' }, { value: 1, label: '1 - ≥ 120/min' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 6 };
    },
    interpret: (score) => {
      if (score <= 1) return { level: 'success', text: `Dakar ${score}`, detail: 'Tétanos bénin. Mortalité < 10%' };
      if (score <= 3) return { level: 'warning', text: `Dakar ${score}`, detail: 'Tétanos modéré. Mortalité 10-30%' };
      return { level: 'danger', text: `Dakar ${score}`, detail: 'Tétanos grave. Mortalité > 30%. Réanimation' };
    },
    formula: 'Dakar: 6 critères pronostiques du tétanos'
  },

  altemeier: {
    name: 'Classification d\'Altemeier',
    category: 'Infectiologie',
    icon: Thermometer,
    description: 'Risque infectieux chirurgical',
    color: 'rose',
    fields: [
      { id: 'class', label: 'Classe de contamination', type: 'select', options: [
        { value: 1, label: 'I - Propre (peau intacte, sans inflammation, fermeture primaire)' },
        { value: 2, label: 'II - Propre-contaminée (tube digestif, respiratoire, urinaire sans infection)' },
        { value: 3, label: 'III - Contaminée (plaie trauma < 4h, rupture digestive, inflammation aiguë)' },
        { value: 4, label: 'IV - Sale ou infectée (perforation, pus, plaie > 4h)' }
      ]}
    ],
    calculate: (values) => ({ score: values.class || 1, max: 4 }),
    interpret: (score) => {
      const risks = ['1-2%', '5-10%', '15-20%', '> 30%'];
      if (score === 1) return { level: 'success', text: 'Altemeier Classe I', detail: `Risque infection: ${risks[0]}. Pas d'antibioprophylaxie sauf prothèse` };
      if (score === 2) return { level: 'success', text: 'Altemeier Classe II', detail: `Risque: ${risks[1]}. Antibioprophylaxie recommandée` };
      if (score === 3) return { level: 'warning', text: 'Altemeier Classe III', detail: `Risque: ${risks[2]}. Antibioprophylaxie ou antibiothérapie` };
      return { level: 'danger', text: 'Altemeier Classe IV', detail: `Risque: ${risks[3]}. Antibiothérapie curative` };
    },
    formula: 'Altemeier I-IV: classification contamination chirurgicale'
  },

  paludismeGravite: {
    name: 'Critères de gravité Paludisme',
    category: 'Infectiologie',
    icon: Thermometer,
    description: 'Paludisme grave à P. falciparum',
    color: 'rose',
    fields: [
      { id: 'coma', label: 'Trouble conscience (GCS < 11)', type: 'checkbox', points: 1 },
      { id: 'convulsions', label: 'Convulsions répétées (≥ 2/24h)', type: 'checkbox', points: 1 },
      { id: 'prostration', label: 'Prostration', type: 'checkbox', points: 1 },
      { id: 'respiratory', label: 'Détresse respiratoire', type: 'checkbox', points: 1 },
      { id: 'shock', label: 'Choc (PAS < 80 mmHg adulte)', type: 'checkbox', points: 1 },
      { id: 'bleeding', label: 'Saignement anormal', type: 'checkbox', points: 1 },
      { id: 'jaundice', label: 'Ictère (bili > 50 µmol/L)', type: 'checkbox', points: 1 },
      { id: 'anemia', label: 'Anémie sévère (Hb < 7 g/dL)', type: 'checkbox', points: 1 },
      { id: 'hypoglycemia', label: 'Hypoglycémie (< 2.2 mmol/L)', type: 'checkbox', points: 1 },
      { id: 'acidosis', label: 'Acidose (pH < 7.35, bicar < 15)', type: 'checkbox', points: 1 },
      { id: 'renal', label: 'Insuffisance rénale', type: 'checkbox', points: 1 },
      { id: 'parasitemia', label: 'Hyperparasitémie (> 4%)', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 12 };
    },
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: 'Paludisme non compliqué', detail: 'Traitement ambulatoire possible (ACT)' };
      return { level: 'danger', text: `Paludisme grave (${score} critère${score > 1 ? 's' : ''})`, detail: 'Hospitalisation urgente. Artésunate IV' };
    },
    formula: 'OMS: ≥ 1 critère = paludisme grave'
  },

  callCovid: {
    name: 'Score CALL (COVID-19)',
    category: 'Infectiologie',
    icon: Thermometer,
    description: 'Risque d\'évolution défavorable COVID-19',
    color: 'rose',
    fields: [
      { id: 'comorbidity', label: 'Comorbidité (HTA, diabète, cardiopathie, BPCO)', type: 'checkbox', points: 1 },
      { id: 'age', label: 'Âge ≥ 60 ans', type: 'checkbox', points: 1 },
      { id: 'lymphocytes', label: 'Lymphocytes < 1000/mm³', type: 'checkbox', points: 1 },
      { id: 'ldh', label: 'LDH > 250 UI/L', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 4 };
    },
    interpret: (score) => {
      if (score <= 1) return { level: 'success', text: `CALL ${score}/4`, detail: 'Risque faible d\'aggravation' };
      if (score <= 2) return { level: 'warning', text: `CALL ${score}/4`, detail: 'Risque modéré. Surveillance rapprochée' };
      return { level: 'danger', text: `CALL ${score}/4`, detail: 'Risque élevé d\'aggravation. Hospitalisation recommandée' };
    },
    formula: 'CALL = Comorbidity + Age + Lymphocytes + LDH'
  }
};

// ==================== URGENCES ÉTENDU ====================
export const EMERGENCY_EXTENDED_SCORES = {
  ccmu: {
    name: 'Classification CCMU',
    category: 'Urgences',
    icon: Stethoscope,
    description: 'Gravité à l\'entrée aux urgences',
    color: 'orange',
    fields: [
      { id: 'class', label: 'Classification CCMU', type: 'select', options: [
        { value: 1, label: 'CCMU 1 - État lésionnel ou pronostic fonctionnel jugé stable' },
        { value: 2, label: 'CCMU 2 - État lésionnel ou pronostic fonctionnel jugé stable, investigation et thérapeutique' },
        { value: 3, label: 'CCMU 3 - Pronostic fonctionnel atteint, pas de mise en jeu du pronostic vital' },
        { value: 4, label: 'CCMU 4 - Pronostic vital engagé, pas de réanimation immédiate' },
        { value: 5, label: 'CCMU 5 - Pronostic vital engagé, réanimation immédiate' },
        { value: 'P', label: 'CCMU P - Patient avec problème psychiatrique dominant' },
        { value: 'D', label: 'CCMU D - Patient décédé' }
      ]}
    ],
    calculate: (values) => ({ score: values.class || 1, max: 5 }),
    interpret: (score) => {
      if (score === 1) return { level: 'success', text: 'CCMU 1', detail: 'Retour à domicile probable après consultation' };
      if (score === 2) return { level: 'success', text: 'CCMU 2', detail: 'Investigations diagnostiques ou thérapeutiques' };
      if (score === 3) return { level: 'warning', text: 'CCMU 3', detail: 'Hospitalisation probable' };
      if (score === 4) return { level: 'danger', text: 'CCMU 4', detail: 'Soins intensifs probables' };
      if (score === 5) return { level: 'danger', text: 'CCMU 5', detail: 'Réanimation immédiate' };
      return { level: 'warning', text: `CCMU ${score}`, detail: 'Classification spécifique' };
    },
    formula: 'CCMU 1-5: Classification Clinique des Malades des Urgences'
  },

  french: {
    name: 'Grille FRENCH',
    category: 'Urgences',
    icon: Stethoscope,
    description: 'Triage à l\'accueil des urgences',
    color: 'orange',
    fields: [
      { id: 'level', label: 'Niveau de tri', type: 'select', options: [
        { value: 1, label: '1 - Détresse vitale immédiate (ACR, choc, détresse respiratoire sévère)' },
        { value: 2, label: '2 - Urgence vraie (douleur intense, hémodynamique instable, AVC)' },
        { value: 3, label: '3 - Urgence relative (fracture simple, fièvre, douleur modérée)' },
        { value: 4, label: '4 - Consultation non programmée (symptômes chroniques, renouvellement)' },
        { value: 5, label: '5 - Consultation programmée (bilan, certificat)' }
      ]}
    ],
    calculate: (values) => ({ score: values.level || 3, max: 5 }),
    interpret: (score) => {
      const delays = ['Immédiat', '< 20 min', '< 60 min', '< 120 min', 'Selon disponibilité'];
      const levels = ['danger', 'danger', 'warning', 'success', 'success'];
      return { level: levels[score - 1], text: `FRENCH niveau ${score}`, detail: `Délai de prise en charge: ${delays[score - 1]}` };
    },
    formula: 'FRENCH: 5 niveaux de tri aux urgences françaises'
  },

  ottawaCephalee: {
    name: 'Règles d\'Ottawa Céphalée',
    category: 'Urgences',
    icon: Brain,
    description: 'Critères évoquant HSA',
    color: 'orange',
    fields: [
      { id: 'age', label: 'Âge ≥ 40 ans', type: 'checkbox', points: 1 },
      { id: 'neckPain', label: 'Douleur ou raideur du cou', type: 'checkbox', points: 1 },
      { id: 'witnessed', label: 'Perte de connaissance', type: 'checkbox', points: 1 },
      { id: 'onset', label: 'Début pendant l\'effort', type: 'checkbox', points: 1 },
      { id: 'thunderclap', label: 'Céphalée en coup de tonnerre (intensité max en < 1 min)', type: 'checkbox', points: 1 },
      { id: 'flexion', label: 'Limitation flexion du cou', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 6 };
    },
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: 'Ottawa Céphalée négatif', detail: 'HSA peu probable (Se 100%). Pas de scanner/PL si GCS 15' };
      return { level: 'danger', text: `Ottawa Céphalée positif (${score} critère${score > 1 ? 's' : ''})`, detail: 'HSA à éliminer. Scanner ± PL' };
    },
    formula: 'Ottawa Céphalée: ≥ 1 critère = scanner/PL pour éliminer HSA'
  },

  ringMessmer: {
    name: 'Classification de Ring et Messmer',
    category: 'Urgences',
    icon: AlertTriangle,
    description: 'Sévérité réaction anaphylactique',
    color: 'orange',
    fields: [
      { id: 'grade', label: 'Grade anaphylaxie', type: 'select', options: [
        { value: 1, label: 'Grade I - Cutanéo-muqueux (urticaire, érythème, œdème)' },
        { value: 2, label: 'Grade II - Modérée (hypotension, tachycardie, bronchospasme)' },
        { value: 3, label: 'Grade III - Menaçant le pronostic vital (choc, bronchospasme sévère)' },
        { value: 4, label: 'Grade IV - Arrêt cardio-respiratoire' }
      ]}
    ],
    calculate: (values) => ({ score: values.grade || 1, max: 4 }),
    interpret: (score) => {
      if (score === 1) return { level: 'warning', text: 'Ring-Messmer Grade I', detail: 'Antihistaminiques, corticoïdes. Surveillance 6h' };
      if (score === 2) return { level: 'warning', text: 'Ring-Messmer Grade II', detail: 'Adrénaline IM 0.3-0.5mg si aggravation. Remplissage' };
      if (score === 3) return { level: 'danger', text: 'Ring-Messmer Grade III', detail: 'Adrénaline IM/IV. Remplissage massif. Réanimation' };
      return { level: 'danger', text: 'Ring-Messmer Grade IV', detail: 'RCP + Adrénaline IV 1mg. ACR' };
    },
    formula: 'Ring-Messmer I-IV: classification anaphylaxie peropératoire'
  },

  hypothermieClasses: {
    name: 'Classification Hypothermie',
    category: 'Urgences',
    icon: Thermometer,
    description: 'Sévérité hypothermie',
    color: 'orange',
    fields: [
      { id: 'temp', label: 'Température centrale (°C)', type: 'number', min: 15, max: 36, step: 0.1 }
    ],
    calculate: (values) => {
      const temp = values.temp || 35;
      let grade;
      if (temp >= 32) grade = 1;
      else if (temp >= 28) grade = 2;
      else if (temp >= 24) grade = 3;
      else grade = 4;
      return { score: grade, max: 4, details: { temp } };
    },
    interpret: (score, result) => {
      const temp = result?.details?.temp;
      if (score === 1) return { level: 'warning', text: 'Hypothermie légère (32-35°C)', detail: 'Réchauffement passif. Surveillance ECG' };
      if (score === 2) return { level: 'danger', text: 'Hypothermie modérée (28-32°C)', detail: 'Réchauffement actif externe. Risque FV' };
      if (score === 3) return { level: 'danger', text: 'Hypothermie sévère (24-28°C)', detail: 'Réchauffement actif interne. Mobilisation prudente' };
      return { level: 'danger', text: 'Hypothermie profonde (<24°C)', detail: 'ECMO/CEC à discuter. Pas de mort avant réchauffement' };
    },
    formula: 'Classification: légère (32-35°C), modérée (28-32°C), sévère (24-28°C), profonde (<24°C)'
  },

  hopeHypothermie: {
    name: 'Score HOPE',
    category: 'Urgences',
    icon: Activity,
    description: 'Survie après ACR hypothermique',
    color: 'orange',
    fields: [
      { id: 'temp', label: 'Température à l\'admission (°C)', type: 'number', min: 10, max: 35, step: 0.1 },
      { id: 'sex', label: 'Sexe masculin', type: 'checkbox', points: 0 },
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 1, max: 100 },
      { id: 'asphyxia', label: 'Mécanisme d\'asphyxie', type: 'checkbox', points: 0 },
      { id: 'potassium', label: 'Kaliémie (mmol/L)', type: 'number', min: 2, max: 15, step: 0.1 },
      { id: 'cpr', label: 'Durée CPR avant ECMO (min)', type: 'number', min: 0, max: 300 }
    ],
    calculate: (values) => {
      // Simplified HOPE score calculation
      const temp = values.temp || 24;
      const age = values.age || 30;
      const k = values.potassium || 6;
      const cpr = values.cpr || 60;
      const asphyxia = values.asphyxia ? 1 : 0;
      const male = values.sex ? 1 : 0;
      
      // Approximation de la survie
      let survival = 90;
      survival -= (k - 4) * 10;
      survival -= (temp > 28 ? 10 : 0);
      survival -= (cpr / 10);
      survival -= (asphyxia * 30);
      survival = Math.max(0, Math.min(100, survival));
      
      return { score: Math.round(survival), max: '%' };
    },
    interpret: (score) => {
      if (score >= 50) return { level: 'success', text: `HOPE: ${score}% survie`, detail: 'Réchauffement par ECMO à considérer fortement' };
      if (score >= 20) return { level: 'warning', text: `HOPE: ${score}% survie`, detail: 'ECMO à discuter selon ressources' };
      return { level: 'danger', text: `HOPE: ${score}% survie`, detail: 'Pronostic très sombre malgré ECMO' };
    },
    formula: 'HOPE: Hypothermia Outcome Prediction after ECLS'
  },

  tripCast: {
    name: 'Score TRiP(cast)',
    category: 'Urgences',
    icon: Droplets,
    description: 'Thromboprophylaxie sur immobilisation',
    color: 'orange',
    fields: [
      { id: 'age', label: 'Âge 30-60 ans', type: 'checkbox', points: 1 },
      { id: 'age60', label: 'Âge > 60 ans', type: 'checkbox', points: 2 },
      { id: 'vte', label: 'ATCD MTEV personnelle', type: 'checkbox', points: 2 },
      { id: 'family', label: 'ATCD MTEV familiale', type: 'checkbox', points: 1 },
      { id: 'oc', label: 'Contraception orale', type: 'checkbox', points: 1 },
      { id: 'surgery', label: 'Chirurgie récente', type: 'checkbox', points: 1 },
      { id: 'cancer', label: 'Cancer actif', type: 'checkbox', points: 2 },
      { id: 'obesity', label: 'Obésité IMC > 30', type: 'checkbox', points: 1 },
      { id: 'immobility', label: 'Immobilisation complète', type: 'checkbox', points: 2 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.age) total += 1;
      if (values.age60) total += 2;
      if (values.vte) total += 2;
      if (values.family) total += 1;
      if (values.oc) total += 1;
      if (values.surgery) total += 1;
      if (values.cancer) total += 2;
      if (values.obesity) total += 1;
      if (values.immobility) total += 2;
      return { score: total, max: 13 };
    },
    interpret: (score) => {
      if (score <= 4) return { level: 'success', text: `TRiP ${score}`, detail: 'Risque faible. Pas de thromboprophylaxie systématique' };
      if (score <= 7) return { level: 'warning', text: `TRiP ${score}`, detail: 'Risque modéré. Thromboprophylaxie à discuter' };
      return { level: 'danger', text: `TRiP ${score}`, detail: 'Risque élevé. Thromboprophylaxie recommandée' };
    },
    formula: 'TRiP(cast): risque thromboembolique sous plâtre'
  },

  autonomieO2: {
    name: 'Autonomie en Oxygène',
    category: 'Urgences',
    icon: Wind,
    description: 'Calcul autonomie bouteille O₂',
    color: 'orange',
    fields: [
      { id: 'volume', label: 'Volume bouteille (L)', type: 'select', options: [
        { value: 2, label: '2 L (type B2)' },
        { value: 5, label: '5 L (type B5)' },
        { value: 10, label: '10 L (type B10)' },
        { value: 15, label: '15 L (type B15)' },
        { value: 50, label: '50 L (type B50)' }
      ]},
      { id: 'pressure', label: 'Pression manomètre (bars)', type: 'number', min: 0, max: 200 },
      { id: 'flow', label: 'Débit prescrit (L/min)', type: 'number', min: 0.5, max: 15, step: 0.5 }
    ],
    calculate: (values) => {
      const volume = values.volume || 5;
      const pressure = values.pressure || 200;
      const flow = values.flow || 3;
      
      // Volume O2 disponible = Volume bouteille × Pression
      const availableO2 = volume * pressure;
      // Autonomie en minutes = Volume disponible / Débit
      const autonomyMin = availableO2 / flow;
      const hours = Math.floor(autonomyMin / 60);
      const minutes = Math.round(autonomyMin % 60);
      
      return { score: Math.round(autonomyMin), max: 'min', details: { hours, minutes, availableO2 } };
    },
    interpret: (score, result) => {
      const h = result?.details?.hours || 0;
      const m = result?.details?.minutes || 0;
      const o2 = result?.details?.availableO2 || 0;
      if (score < 30) return { level: 'danger', text: `Autonomie: ${h}h${m}min`, detail: `${o2}L O₂ disponibles. Changer rapidement la bouteille!` };
      if (score < 60) return { level: 'warning', text: `Autonomie: ${h}h${m}min`, detail: `${o2}L O₂ disponibles. Préparer bouteille de secours` };
      return { level: 'success', text: `Autonomie: ${h}h${m}min`, detail: `${o2}L O₂ disponibles` };
    },
    formula: 'Autonomie (min) = Volume × Pression / Débit'
  }
};

// ==================== RÉANIMATION ÉTENDU ====================
export const ICU_EXTRA_SCORES = {
  contenAVO2: {
    name: 'Contenu A/V en O₂',
    category: 'Réanimation',
    icon: Activity,
    description: 'Calcul contenu et extraction O₂',
    color: 'orange',
    fields: [
      { id: 'hb', label: 'Hémoglobine (g/dL)', type: 'number', min: 5, max: 20, step: 0.1 },
      { id: 'sao2', label: 'SaO₂ (%)', type: 'number', min: 50, max: 100 },
      { id: 'svo2', label: 'SvO₂ (%)', type: 'number', min: 30, max: 90 },
      { id: 'pao2', label: 'PaO₂ (mmHg)', type: 'number', min: 30, max: 500 },
      { id: 'pvo2', label: 'PvO₂ (mmHg)', type: 'number', min: 20, max: 60 }
    ],
    calculate: (values) => {
      const hb = values.hb || 14;
      const sao2 = (values.sao2 || 98) / 100;
      const svo2 = (values.svo2 || 75) / 100;
      const pao2 = values.pao2 || 90;
      const pvo2 = values.pvo2 || 40;
      
      // CaO2 = (1.34 × Hb × SaO2) + (0.003 × PaO2)
      const cao2 = (1.34 * hb * sao2) + (0.003 * pao2);
      // CvO2 = (1.34 × Hb × SvO2) + (0.003 × PvO2)
      const cvo2 = (1.34 * hb * svo2) + (0.003 * pvo2);
      // DAV-O2 = CaO2 - CvO2
      const dav = cao2 - cvo2;
      // Extraction O2 = DAV / CaO2
      const extraction = (dav / cao2 * 100);
      
      return { 
        score: cao2.toFixed(1), 
        max: 'mL/dL', 
        details: { cao2: cao2.toFixed(1), cvo2: cvo2.toFixed(1), dav: dav.toFixed(1), extraction: extraction.toFixed(0) } 
      };
    },
    interpret: (score, result) => {
      const d = result?.details || {};
      const ext = parseFloat(d.extraction);
      let level = 'success';
      let msg = 'Normal';
      if (ext > 30) { level = 'warning'; msg = 'Extraction élevée - choc possible'; }
      if (ext > 40) { level = 'danger'; msg = 'Extraction très élevée - choc'; }
      return { level, text: `CaO₂: ${d.cao2} mL/dL`, detail: `CvO₂: ${d.cvo2} | DAV-O₂: ${d.dav} | Extraction: ${d.extraction}%. ${msg}` };
    },
    formula: 'CaO₂ = 1.34×Hb×SaO₂ + 0.003×PaO₂ | Extraction = (CaO₂-CvO₂)/CaO₂'
  },

  gradientAaO2: {
    name: 'Gradient Alvéolo-artériel O₂',
    category: 'Réanimation',
    icon: Activity,
    description: 'Gradient A-a O₂',
    color: 'orange',
    fields: [
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 18, max: 100 },
      { id: 'fio2', label: 'FiO₂ (%)', type: 'number', min: 21, max: 100 },
      { id: 'pao2', label: 'PaO₂ (mmHg)', type: 'number', min: 30, max: 500 },
      { id: 'paco2', label: 'PaCO₂ (mmHg)', type: 'number', min: 15, max: 100 },
      { id: 'patm', label: 'Pression atmosphérique (mmHg)', type: 'number', min: 700, max: 780 }
    ],
    calculate: (values) => {
      const age = values.age || 40;
      const fio2 = (values.fio2 || 21) / 100;
      const pao2 = values.pao2 || 90;
      const paco2 = values.paco2 || 40;
      const patm = values.patm || 760;
      const ph2o = 47; // Pression vapeur d'eau à 37°C
      
      // PAO2 = FiO2 × (Patm - PH2O) - PaCO2/0.8
      const pao2Alv = fio2 * (patm - ph2o) - (paco2 / 0.8);
      // Gradient = PAO2 - PaO2
      const gradient = pao2Alv - pao2;
      // Gradient normal = (Age + 10) / 4
      const normalGradient = (age + 10) / 4;
      
      return { 
        score: gradient.toFixed(0), 
        max: 'mmHg', 
        details: { pao2Alv: pao2Alv.toFixed(0), normal: normalGradient.toFixed(0) } 
      };
    },
    interpret: (score, result) => {
      const d = result?.details || {};
      const normal = parseFloat(d.normal);
      const grad = parseFloat(score);
      if (grad <= normal + 5) return { level: 'success', text: `Gradient A-a: ${score} mmHg`, detail: `PAO₂: ${d.pao2Alv} mmHg. Normal pour l'âge (≤${d.normal})` };
      if (grad <= normal + 15) return { level: 'warning', text: `Gradient A-a: ${score} mmHg`, detail: `PAO₂: ${d.pao2Alv} mmHg. Légèrement augmenté` };
      return { level: 'danger', text: `Gradient A-a: ${score} mmHg`, detail: `PAO₂: ${d.pao2Alv} mmHg. Augmenté (shunt, trouble V/Q)` };
    },
    formula: 'A-a = FiO₂×(760-47) - PaCO₂/0.8 - PaO₂ | Normal: (Âge+10)/4'
  },

  winterFormula: {
    name: 'Formule de Winter',
    category: 'Réanimation',
    icon: Activity,
    description: 'Compensation respiratoire acidose métabolique',
    color: 'orange',
    fields: [
      { id: 'hco3', label: 'HCO₃⁻ (mmol/L)', type: 'number', min: 5, max: 35 },
      { id: 'paco2', label: 'PaCO₂ mesurée (mmHg)', type: 'number', min: 10, max: 80 }
    ],
    calculate: (values) => {
      const hco3 = values.hco3 || 15;
      const paco2 = values.paco2 || 30;
      
      // PaCO2 attendue = 1.5 × HCO3 + 8 (± 2)
      const expectedPaco2 = 1.5 * hco3 + 8;
      const diff = paco2 - expectedPaco2;
      
      return { 
        score: expectedPaco2.toFixed(0), 
        max: 'mmHg', 
        details: { expected: expectedPaco2.toFixed(0), measured: paco2, diff: diff.toFixed(1) } 
      };
    },
    interpret: (score, result) => {
      const d = result?.details || {};
      const diff = parseFloat(d.diff);
      if (Math.abs(diff) <= 2) return { level: 'success', text: `PaCO₂ attendue: ${d.expected} mmHg`, detail: `Mesurée: ${d.measured}. Compensation respiratoire adaptée` };
      if (diff > 2) return { level: 'warning', text: `PaCO₂ attendue: ${d.expected} mmHg`, detail: `Mesurée: ${d.measured}. PaCO₂ trop haute → Alcalose respiratoire associée ou compensation insuffisante` };
      return { level: 'warning', text: `PaCO₂ attendue: ${d.expected} mmHg`, detail: `Mesurée: ${d.measured}. PaCO₂ trop basse → Acidose respiratoire associée` };
    },
    formula: 'Winter: PaCO₂ attendue = 1.5 × HCO₃⁻ + 8 (± 2)'
  }
};

export const INFECTIOUS_EMERGENCY_ICU_SCORES = {
  ...INFECTIOUS_EXTENDED_SCORES,
  ...EMERGENCY_EXTENDED_SCORES,
  ...ICU_EXTRA_SCORES
};

export default INFECTIOUS_EMERGENCY_ICU_SCORES;
